import React, { useState } from 'react';
import {
  X,
  BookOpen,
  QrCode,
  Cloud,
  CheckCircle2,
  HelpCircle,
  ExternalLink,
  ShieldAlert,
  CreditCard,
  DollarSign,
  Smartphone,
  Info,
  Building2,
  Sparkles,
} from 'lucide-react';

interface WhatsAppCloudGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultTab?: 'escenarios' | 'paso_a_paso' | 'precios' | 'politicas' | 'faq';
}

export const WhatsAppCloudGuideModal: React.FC<WhatsAppCloudGuideModalProps> = ({
  isOpen,
  onClose,
  defaultTab = 'escenarios',
}) => {
  const [activeTab, setActiveTab] = useState<'escenarios' | 'paso_a_paso' | 'precios' | 'politicas' | 'faq'>(
    defaultTab
  );

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-black/60 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Encabezado */}
        <div className="bg-gradient-to-r from-emerald-700 via-emerald-800 to-teal-900 text-white p-5 sm:p-6 shrink-0 relative">
          <button
            type="button"
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-colors"
            title="Cerrar guía"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2.5 text-emerald-200 text-xs font-semibold uppercase tracking-wider mb-1">
            <BookOpen className="w-4 h-4" />
            <span>Centro de Guías Oficiales & Soporte</span>
          </div>
          <h3 className="text-xl sm:text-2xl font-bold tracking-tight">
            Guía de Conexión: WhatsApp Cloud API vs Código QR
          </h3>
          <p className="text-xs sm:text-sm text-emerald-100/90 mt-1 max-w-2xl leading-relaxed">
            Aprende a elegir el canal adecuado, migrar tu número sin perder datos, configurar tu cuenta en Meta y entender las políticas y costos oficiales.
          </p>

          {/* Navegación por pestañas */}
          <div className="flex items-center gap-1.5 mt-5 overflow-x-auto pb-1 scrollbar-none text-xs sm:text-sm">
            <button
              type="button"
              onClick={() => setActiveTab('escenarios')}
              className={`px-3.5 py-1.5 rounded-lg font-semibold transition-all shrink-0 flex items-center gap-1.5 ${
                activeTab === 'escenarios'
                  ? 'bg-white text-emerald-900 shadow-sm'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <Smartphone className="w-4 h-4" />
              <span>1. Escenarios & ¿QR o Cloud?</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('paso_a_paso')}
              className={`px-3.5 py-1.5 rounded-lg font-semibold transition-all shrink-0 flex items-center gap-1.5 ${
                activeTab === 'paso_a_paso'
                  ? 'bg-white text-emerald-900 shadow-sm'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>2. Paso a Paso en Meta</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('precios')}
              className={`px-3.5 py-1.5 rounded-lg font-semibold transition-all shrink-0 flex items-center gap-1.5 ${
                activeTab === 'precios'
                  ? 'bg-white text-emerald-900 shadow-sm'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <DollarSign className="w-4 h-4" />
              <span>3. Costos & Facturación Meta</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('politicas')}
              className={`px-3.5 py-1.5 rounded-lg font-semibold transition-all shrink-0 flex items-center gap-1.5 ${
                activeTab === 'politicas'
                  ? 'bg-white text-emerald-900 shadow-sm'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <ShieldAlert className="w-4 h-4" />
              <span>4. Políticas & Restricciones</span>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('faq')}
              className={`px-3.5 py-1.5 rounded-lg font-semibold transition-all shrink-0 flex items-center gap-1.5 ${
                activeTab === 'faq'
                  ? 'bg-white text-emerald-900 shadow-sm'
                  : 'bg-white/10 text-white hover:bg-white/20'
              }`}
            >
              <HelpCircle className="w-4 h-4" />
              <span>5. Preguntas Frecuentes</span>
            </button>
          </div>
        </div>

        {/* Cuerpo del Modal con scroll */}
        <div className="p-5 sm:p-6 overflow-y-auto space-y-6 text-gray-700 text-sm">
          {/* TAB 1: ESCENARIOS & QR VS CLOUD */}
          {activeTab === 'escenarios' && (
            <div className="space-y-6">
              {/* Comparador Visual */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Opción QR */}
                <div className="p-5 rounded-2xl border-2 border-emerald-500/30 bg-emerald-50/40 space-y-3 relative overflow-hidden">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-600 text-white text-xs font-bold">
                    <QrCode className="w-3.5 h-3.5" />
                    <span>Opción A: Conexión QR (WhatsApp Web)</span>
                  </div>
                  <h4 className="text-base font-bold text-gray-900">
                    Conserva tu WhatsApp Business en el celular
                  </h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Ideal para negocios que ya tienen su aplicación móvil con chats, catálogos locales y fotos y no desean borrar nada.
                  </p>
                  <ul className="space-y-2 text-xs text-gray-700 pt-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>No eliminas tu app:</strong> Tu WhatsApp en el teléfono sigue funcionando con normalidad.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>Cero costo por mensaje:</strong> No aplican tarifas de Meta por conversación.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>Ventas automáticas:</strong> El bot de Iridia Chat atiende los clientes y gestiona el embudo.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span><strong>Vinculación fácil:</strong> Solo escaneas un código QR desde <em>Dispositivos vinculados</em>.</span>
                    </li>
                  </ul>
                </div>

                {/* Opción Cloud API */}
                <div className="p-5 rounded-2xl border-2 border-blue-500/30 bg-blue-50/40 space-y-3 relative overflow-hidden">
                  <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#1877F2] text-white text-xs font-bold">
                    <Cloud className="w-3.5 h-3.5" />
                    <span>Opción B: WhatsApp Cloud API + Coexistencia Meta</span>
                  </div>
                  <h4 className="text-base font-bold text-gray-900">
                    Nube Oficial de Meta sin borrar tu App Móvil
                  </h4>
                  <p className="text-xs text-gray-600 leading-relaxed">
                    Con la función oficial de <strong>Coexistencia de Meta</strong>, conectas tu WhatsApp Business actual a la Cloud API manteniendo tu app, catálogo y chats en el teléfono.
                  </p>
                  <ul className="space-y-2 text-xs text-gray-700 pt-2">
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                      <span><strong>Coexistencia (Sin borrar nada):</strong> Conservas tu WhatsApp Business, catálogo móvil, llamadas y estados en tu celular.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                      <span><strong>Importa tu historial:</strong> Sincroniza contactos y los últimos 6 meses de chats escaneando el QR oficial de Meta.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                      <span><strong>1.000 conversaciones gratis/mes:</strong> Incluidas directamente por Meta cada mes.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                      <span><strong>Socio Oficial:</strong> Iridia Chat se vincula como Socio en tu Portafolio Comercial de Meta.</span>
                    </li>
                  </ul>
                </div>
              </div>

              {/* Matriz de Escenarios Prácticos */}
              <div className="space-y-3">
                <h4 className="font-bold text-gray-900 text-sm flex items-center gap-2">
                  <span>🎯</span> Encuentra tu escenario exacto:
                </h4>

                <div className="space-y-2.5">
                  <div className="p-3.5 rounded-xl border border-gray-200 bg-gray-50/80 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <span className="p-2 rounded-lg bg-blue-100 text-blue-800 font-bold text-xs shrink-0">
                        Caso 1
                      </span>
                      <div className="space-y-1">
                        <p className="font-semibold text-gray-900 text-xs sm:text-sm">
                          &ldquo;Ya tengo mi número en WhatsApp Business con catálogo y chats, y quiero usar Cloud API oficial sin borrar nada&rdquo;
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          <strong>Solución oficial (Coexistencia de Meta):</strong> Haz clic en <strong>Conectar con WhatsApp</strong> e ingresa tu número actual. Meta detectará tu app móvil y te mostrará un <strong>código QR oficial de Meta</strong> (<em>&ldquo;Importar contactos e historial de chat&rdquo;</em>). Lo escaneas desde tu app de WhatsApp Business (versión 2.24.17+) y <strong>no se borra nada</strong>: sigues usando tu celular con tu catálogo y chats, y el bot responde en paralelo.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl border border-gray-200 bg-gray-50/80 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <span className="p-2 rounded-lg bg-emerald-100 text-emerald-800 font-bold text-xs shrink-0">
                        Caso 2
                      </span>
                      <div className="space-y-1">
                        <p className="font-semibold text-gray-900 text-xs sm:text-sm">
                          &ldquo;No tengo cuenta de Facebook ni quiero configurar Meta Business Manager&rdquo;
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          <strong>Solución rápida:</strong> Usa el canal <strong>WhatsApp (QR)</strong> directo de Iridia Chat. Escaneas el código QR en 10 segundos desde <em>Dispositivos vinculados</em> sin necesidad de entrar a Facebook ni crear portafolio en Meta.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl border border-gray-200 bg-gray-50/80 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <span className="p-2 rounded-lg bg-purple-100 text-purple-800 font-bold text-xs shrink-0">
                        Caso 3
                      </span>
                      <div className="space-y-1">
                        <p className="font-semibold text-gray-900 text-xs sm:text-sm">
                          &ldquo;Tengo un número nuevo o línea limpia que no tiene WhatsApp instalado&rdquo;
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          <strong>Solución ideal:</strong> Conéctalo en <strong>WhatsApp Cloud API</strong>. Meta te enviará un código de 6 dígitos por SMS o llamada de voz y el número quedará operando 100% en la nube oficial de Meta.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-3.5 rounded-xl border border-gray-200 bg-gray-50/80 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start gap-3">
                      <span className="p-2 rounded-lg bg-amber-100 text-amber-800 font-bold text-xs shrink-0">
                        Caso 4
                      </span>
                      <div className="space-y-1">
                        <p className="font-semibold text-gray-900 text-xs sm:text-sm">
                          &ldquo;No tengo Portafolio Comercial en Meta todavía, pero quiero usar Cloud API&rdquo;
                        </p>
                        <p className="text-xs text-gray-600 leading-relaxed">
                          Cuando hagas clic en <em>Conectar con WhatsApp</em>, el asistente de Meta te permitirá crear tu Portafolio Comercial en el mismo popup en menos de 1 minuto.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: PASO A PASO EN META */}
          {activeTab === 'paso_a_paso' && (
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-blue-50/60 border border-blue-100 text-xs text-blue-900 flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold text-blue-950">Proceso Oficial de Meta Embedded Signup</p>
                  <p className="mt-0.5 leading-relaxed text-blue-800">
                    Este flujo se realiza dentro de una ventana segura protegida por Facebook. Tus credenciales nunca son vistas por Iridia Chat; Meta nos entrega un token cifrado para operar las respuestas del bot.
                  </p>
                </div>
              </div>

              {/* Stepper Visual */}
              <div className="space-y-4">
                {/* Paso 1 */}
                <div className="flex gap-4 p-4 rounded-xl border border-gray-200 bg-white shadow-xs">
                  <div className="w-8 h-8 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center shrink-0 text-sm">
                    1
                  </div>
                  <div className="space-y-2 flex-1">
                    <h5 className="font-bold text-gray-900 text-sm">
                      Iniciar sesión con Facebook y Seleccionar Portafolio
                    </h5>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Haz clic en el botón azul <strong>&ldquo;Conectar con WhatsApp&rdquo;</strong>. Se abrirá la ventana emergente de Meta:
                    </p>
                    <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs text-amber-900">
                      <strong>⚠️ Importante si ya te habías conectado antes:</strong> Haz clic en el botón blanco de la izquierda <em>&ldquo;Editar configuración&rdquo;</em> (NO en <em>Continuar</em>) para asegurarte de marcar tu cuenta de empresa y WhatsApp.
                    </div>
                  </div>
                </div>

                {/* Paso 2 */}
                <div className="flex gap-4 p-4 rounded-xl border border-gray-200 bg-white shadow-xs">
                  <div className="w-8 h-8 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center shrink-0 text-sm">
                    2
                  </div>
                  <div className="space-y-2 flex-1">
                    <h5 className="font-bold text-gray-900 text-sm">
                      Configurar Perfil y Nombre para Mostrar (Display Name)
                    </h5>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Meta te pedirá el nombre público que verán tus clientes en WhatsApp:
                    </p>
                    <ul className="list-disc pl-4 text-xs text-gray-600 space-y-1">
                      <li>Debe coincidir con la marca de tu negocio (ej. <em>Mercado Naranja</em>).</li>
                      <li>Selecciona la categoría comercial correspondiente (ej. <em>Comercio minorista</em>).</li>
                      <li>Indica la zona horaria y dirección del negocio.</li>
                    </ul>
                  </div>
                </div>

                {/* Paso 3 */}
                <div className="flex gap-4 p-4 rounded-xl border border-gray-200 bg-white shadow-xs">
                  <div className="w-8 h-8 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center shrink-0 text-sm">
                    3
                  </div>
                  <div className="space-y-2 flex-1">
                    <h5 className="font-bold text-gray-900 text-sm">
                      Ingresar Número: Escaneo QR de Coexistencia o Código SMS
                    </h5>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Ingresa el código de país (ej. +58 para Venezuela) y tu número de teléfono:
                    </p>
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200 text-xs text-blue-900 space-y-1.5">
                      <p>
                        <strong>• Si tu número YA tiene WhatsApp Business en el celular (Coexistencia):</strong> Meta mostrará la pantalla <em>&ldquo;Importar contactos e historial de chat&rdquo;</em> con un <strong>código QR</strong> y te enviará un mensaje desde la cuenta oficial de <em>Facebook Business</em> a tu WhatsApp. Escanea el código QR desde tu app de WhatsApp Business para sincronizar tus contactos y los últimos 6 meses de chats sin borrar nada de tu teléfono.
                      </p>
                      <p>
                        <strong>• Si es un número nuevo (sin app móvil):</strong> Meta te enviará un código de 6 dígitos por <strong>SMS o llamada de voz</strong> para activarlo directamente en la nube.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Paso 4 */}
                <div className="flex gap-4 p-4 rounded-xl border border-gray-200 bg-white shadow-xs">
                  <div className="w-8 h-8 rounded-full bg-emerald-600 text-white font-bold flex items-center justify-center shrink-0 text-sm">
                    4
                  </div>
                  <div className="space-y-2 flex-1">
                    <h5 className="font-bold text-gray-900 text-sm">
                      Finalización y Cierre Automático
                    </h5>
                    <p className="text-xs text-gray-600 leading-relaxed">
                      Aparecerá una pantalla con confeti diciendo <em>&ldquo;Te has conectado a IridiaChat&rdquo;</em>. La ventana se cerrará automáticamente, Iridia Chat quedará registrado como <strong>Socio</strong> en tu cuenta comercial y tu canal pasará a <strong className="text-emerald-700">Conectado (ACTIVE)</strong>.
                    </p>
                  </div>
                </div>
              </div>

              {/* Enlaces directos a documentación oficial de Meta */}
              <div className="flex flex-wrap items-center justify-between gap-3 p-4 rounded-xl border border-gray-200 bg-gray-50 text-xs">
                <span className="text-gray-600">
                  Documentación oficial de Meta sobre Coexistencia y Embedded Signup:
                </span>
                <div className="flex flex-wrap items-center gap-4">
                  <a
                    href="https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/onboarding-business-app-users"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                  >
                    Guía de Coexistencia (App + Cloud) <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <a
                    href="https://developers.facebook.com/documentation/business-messaging/whatsapp/embedded-signup/overview"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-bold text-blue-600 hover:text-blue-800 inline-flex items-center gap-1"
                  >
                    Embedded Signup Docs <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: PRECIOS Y FACTURACIÓN */}
          {activeTab === 'precios' && (
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-emerald-50/70 border border-emerald-200 text-xs text-emerald-950 space-y-1.5">
                <p className="font-bold text-emerald-900 text-sm flex items-center gap-1.5">
                  <Sparkles className="w-4 h-4 text-emerald-600" />
                  Meta otorga 1.000 conversaciones de servicio gratuitas cada mes
                </p>
                <p className="text-emerald-800 leading-relaxed">
                  Cada cuenta de WhatsApp Business (WABA) recibe <strong>1.000 conversaciones iniciadas por el cliente gratis</strong> todos los meses. Para la mayoría de los negocios pequeños y medianos, esto cubre la totalidad de sus ventas sin costo adicional de Meta.
                </p>
              </div>

              {/* Estructura de Cobro de Meta */}
              <div className="space-y-3">
                <h4 className="font-bold text-gray-900 text-sm">¿Cómo cobra Meta por las conversaciones?</h4>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Meta no cobra por mensaje individual, sino por <strong>sesión de conversación de 24 horas</strong>. Durante esa ventana de 24 horas, el bot y el cliente pueden enviarse infinitos mensajes sin costo adicional.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                  <div className="p-3.5 rounded-xl border border-gray-200 bg-white space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-gray-900 text-xs">Conversación de Servicio</span>
                      <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-md">
                        1.000 gratis/mes
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Cuando el cliente escribe primero para consultar precios, stock o hacer un pedido. Luego de las 1.000 gratis, cuesta aprox. <strong>~$0.01 a $0.03 USD</strong> según el país.
                    </p>
                  </div>

                  <div className="p-3.5 rounded-xl border border-gray-200 bg-white space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-gray-900 text-xs">Conversación de Utilidad</span>
                      <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-0.5 rounded-md">
                        Tarifa fija baja
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Notificaciones transaccionales automáticas: confirmación de compra, recibos de pago o estado de envío (~$0.01 USD).
                    </p>
                  </div>

                  <div className="p-3.5 rounded-xl border border-gray-200 bg-white space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-gray-900 text-xs">Conversación de Marketing</span>
                      <span className="text-xs font-semibold text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md">
                        Tarifa comercial
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Mensajes masivos o promocionales enviados por la empresa a clientes que no han escrito en las últimas 24 horas (~$0.03 a $0.06 USD).
                    </p>
                  </div>

                  <div className="p-3.5 rounded-xl border border-gray-200 bg-white space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-gray-900 text-xs">Conversación de Autenticación</span>
                      <span className="text-xs font-semibold text-gray-600 bg-gray-100 px-2 py-0.5 rounded-md">
                        Tarifa mínima
                      </span>
                    </div>
                    <p className="text-xs text-gray-600">
                      Envío de códigos de seguridad OTP o restablecimiento de contraseñas (~$0.01 USD).
                    </p>
                  </div>
                </div>
              </div>

              {/* Dónde se paga y configura la tarjeta */}
              <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/70 space-y-3">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-gray-700" />
                  <h5 className="font-bold text-gray-900 text-xs sm:text-sm">
                    ¿Dónde se asocia la tarjeta para los cobros de Meta?
                  </h5>
                </div>
                <p className="text-xs text-gray-600 leading-relaxed">
                  <strong>Iridia Chat NO cobra comisiones por mensaje.</strong> El consumo de la API lo cobra Meta directamente a tu método de pago registrado en tu Business Manager.
                </p>
                <div className="flex flex-wrap items-center gap-3 pt-1">
                  <a
                    href="https://business.facebook.com/billing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 hover:bg-black text-white text-xs font-medium transition-colors"
                  >
                    <span>Configurar Facturación en Meta</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                  <a
                    href="https://business.whatsapp.com/pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-gray-300 hover:bg-gray-100 text-gray-800 text-xs font-medium transition-colors"
                  >
                    <span>Calculadora Oficial de Tarifas de Meta</span>
                    <ExternalLink className="w-3.5 h-3.5" />
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* TAB 4: POLÍTICAS Y RESTRICCIONES */}
          {activeTab === 'politicas' && (
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-amber-50/80 border border-amber-200 text-xs text-amber-950 flex items-start gap-3">
                <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="space-y-1">
                  <p className="font-bold text-amber-900">Cumplimiento de la Política Comercial de WhatsApp</p>
                  <p className="leading-relaxed text-amber-800">
                    Meta revisa de manera automatizada las cuentas y números comerciales. El incumplimiento de sus políticas puede ocasionar la suspensión de la cuenta de WhatsApp Business.
                  </p>
                </div>
              </div>

              {/* Categorías prohibidas */}
              <div className="space-y-3">
                <h4 className="font-bold text-gray-900 text-sm">Productos y Servicios No Permitidos por Meta:</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs text-gray-700">
                  <div className="p-2.5 rounded-lg border border-red-100 bg-red-50/40 flex items-center gap-2">
                    <span className="text-red-600 font-bold">✕</span> Medicamentos bajo receta y drogas
                  </div>
                  <div className="p-2.5 rounded-lg border border-red-100 bg-red-50/40 flex items-center gap-2">
                    <span className="text-red-600 font-bold">✕</span> Tabaco, cigarrillos y vapes
                  </div>
                  <div className="p-2.5 rounded-lg border border-red-100 bg-red-50/40 flex items-center gap-2">
                    <span className="text-red-600 font-bold">✕</span> Armas, municiones y explosivos
                  </div>
                  <div className="p-2.5 rounded-lg border border-red-100 bg-red-50/40 flex items-center gap-2">
                    <span className="text-red-600 font-bold">✕</span> Productos o servicios para adultos
                  </div>
                  <div className="p-2.5 rounded-lg border border-red-100 bg-red-50/40 flex items-center gap-2">
                    <span className="text-red-600 font-bold">✕</span> Juegos de azar y apuestas con dinero real
                  </div>
                  <div className="p-2.5 rounded-lg border border-red-100 bg-red-50/40 flex items-center gap-2">
                    <span className="text-red-600 font-bold">✕</span> Animales vivos (salvo adopción autorizada)
                  </div>
                </div>
              </div>

              {/* Reglas del Nombre para Mostrar */}
              <div className="p-4 rounded-xl border border-gray-200 bg-white space-y-2">
                <h5 className="font-bold text-gray-900 text-xs sm:text-sm">
                  Reglas de Meta para el Nombre para Mostrar (Display Name):
                </h5>
                <ul className="list-disc pl-4 text-xs text-gray-600 space-y-1 leading-relaxed">
                  <li><strong>Debe representar a la empresa:</strong> No puede ser un nombre genérico (ej: <em>&ldquo;Comida Rápida&rdquo;</em> será rechazado; <em>&ldquo;Burger House Caracas&rdquo;</em> será aprobado).</li>
                  <li><strong>Sin mayúsculas excesivas:</strong> No escribas TODO EN MAYÚSCULAS salvo que sea un acrónimo legalmente registrado.</li>
                  <li><strong>Sin emojis ni caracteres decorativos:</strong> Meta prohíbe símbolos como ✨, 🔥 o emojis en el nombre oficial.</li>
                </ul>
              </div>

              {/* Límites de Mensajería y Calidad */}
              <div className="p-4 rounded-xl border border-gray-200 bg-gray-50/80 space-y-2">
                <h5 className="font-bold text-gray-900 text-xs sm:text-sm">
                  Límites de Envío de Mensajes (Tiers de Mensajería):
                </h5>
                <p className="text-xs text-gray-600 leading-relaxed">
                  Meta asigna niveles automáticos según la calidad y volumen de envíos:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center text-xs pt-1">
                  <div className="p-2 rounded-lg bg-white border border-gray-200 font-medium">
                    <span className="block font-bold text-emerald-700">Tier 1</span>
                    1.000 clientes/día
                  </div>
                  <div className="p-2 rounded-lg bg-white border border-gray-200 font-medium">
                    <span className="block font-bold text-blue-700">Tier 2</span>
                    10.000 clientes/día
                  </div>
                  <div className="p-2 rounded-lg bg-white border border-gray-200 font-medium">
                    <span className="block font-bold text-purple-700">Tier 3</span>
                    100.000 clientes/día
                  </div>
                  <div className="p-2 rounded-lg bg-white border border-gray-200 font-medium">
                    <span className="block font-bold text-amber-700">Tier 4</span>
                    Ilimitado
                  </div>
                </div>
              </div>

              {/* Enlace a políticas oficiales */}
              <div className="flex items-center justify-between p-3.5 rounded-xl border border-gray-200 bg-gray-50 text-xs">
                <span className="text-gray-600">Revisa la normativa legal completa de WhatsApp:</span>
                <a
                  href="https://www.whatsapp.com/legal/commerce-policy/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-bold text-emerald-700 hover:text-emerald-900 inline-flex items-center gap-1"
                >
                  Política de Comercio de WhatsApp <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            </div>
          )}

          {/* TAB 5: PREGUNTAS FRECUENTES (FAQ) */}
          {activeTab === 'faq' && (
            <div className="space-y-4">
              <div className="p-4 rounded-xl border border-gray-200 bg-white space-y-1.5">
                <p className="font-bold text-gray-900 text-xs sm:text-sm flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-emerald-600" />
                  ¿Puedo seguir usando la app de WhatsApp Business en mi celular si me conecto por Cloud API?
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-6">
                  <strong>¡Sí! Gracias a la función oficial de Coexistencia de Meta</strong> (disponible en WhatsApp Business versión 2.24.17 o superior), al escanear el código QR de Meta durante el registro no tienes que borrar tu app móvil. Conservas tu <strong>catálogo nativo, pedidos, etiquetas, estados, llamadas y los últimos 6 meses de chats</strong> en tu teléfono mientras el bot responde en paralelo. Solo debes abrir la app en el teléfono al menos una vez cada 13 días para mantener activa la sincronización.
                </p>
              </div>

              <div className="p-4 rounded-xl border border-gray-200 bg-white space-y-1.5">
                <p className="font-bold text-gray-900 text-xs sm:text-sm flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-emerald-600" />
                  ¿Por qué aparece Iridia Chat en la pestaña &ldquo;Socios&rdquo; de mi Meta Business Manager?
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-6">
                  Cuando vinculas tu cuenta mediante Embedded Signup, Meta establece una relación oficial de <strong>Socio Tecnológico (Tech Provider)</strong> entre tu Portafolio Comercial e Iridia Chat. Esto permite que nuestro bot reciba y envíe mensajes de forma segura sin que tengas que compartir tus contraseñas.
                </p>
              </div>

              <div className="p-4 rounded-xl border border-gray-200 bg-white space-y-1.5">
                <p className="font-bold text-gray-900 text-xs sm:text-sm flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-emerald-600" />
                  ¿Cómo consigo la tilde verde de verificación oficial (Official Business Account)?
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-6">
                  La tilde verde la otorga Meta tras verificar tu empresa en Meta Business Suite y demostrar notoriedad de marca (presencia en medios o búsquedas relevantes). Requiere usar Cloud API y solicitar la verificación desde el panel de WhatsApp Manager.
                </p>
              </div>

              <div className="p-4 rounded-xl border border-gray-200 bg-white space-y-1.5">
                <p className="font-bold text-gray-900 text-xs sm:text-sm flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-emerald-600" />
                  ¿Qué pasa si respondo un mensaje desde mi celular estando en modo Coexistencia?
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-6">
                  Meta sincroniza en tiempo real los mensajes enviados desde tu teléfono hacia Iridia Chat (mediante eventos <code>smb_message_echoes</code>), y todas las respuestas que envíe el bot de Iridia Chat aparecerán inmediatamente en la conversación de tu teléfono celular.
                </p>
              </div>

              <div className="p-4 rounded-xl border border-gray-200 bg-white space-y-1.5">
                <p className="font-bold text-gray-900 text-xs sm:text-sm flex items-center gap-2">
                  <HelpCircle className="w-4 h-4 text-emerald-600" />
                  ¿Dónde gestiono mis catálogos de productos?
                </p>
                <p className="text-xs text-gray-600 leading-relaxed pl-6">
                  Con la Coexistencia activa, tu <strong>catálogo nativo dentro de la app de WhatsApp Business del celular sigue funcionando normalmente</strong>. Además, en Iridia Chat gestionas el inventario en el módulo de <strong>Catálogo</strong> para que el bot de IA consulte precios, stock y fotos al instante durante la conversación.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Pie del Modal con acciones */}
        <div className="p-4 bg-gray-50 border-t border-gray-200 flex flex-wrap items-center justify-between gap-3 shrink-0">
          <div className="text-xs text-gray-500 flex items-center gap-1.5">
            <Building2 className="w-4 h-4 text-gray-400" />
            <span>Iridia Chat • WhatsApp Cloud API Tech Provider</span>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-semibold shadow-xs transition-colors"
          >
            Entendido, volver a la configuración
          </button>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppCloudGuideModal;
